import { captureException } from '@sentry/nextjs'
import Cookies from 'js-cookie'
import { createContext, useContext, useEffect, useState } from 'react'
import { v4 as getUuid } from 'uuid'

import { COOKIE_NAMES } from 'lib/constants'
import { segmentAnalytics } from 'lib/segmentAnalytics'
import { callFunctionProbabilistically } from 'utils/callFunctionProbabilistically'

const createNewAnonymousId = (): string => getUuid()

const ANONYMOUS_ID_CONTEXT_INITIAL_VALUE = Symbol('ANONYMOUS_ID_INITIAL_VALUE')
type InitialValue = typeof ANONYMOUS_ID_CONTEXT_INITIAL_VALUE

const AnonymousIdContext = createContext<InitialValue | string | null>(
  ANONYMOUS_ID_CONTEXT_INITIAL_VALUE,
)

export const AnonymousIdProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [anonymousId, setAnonymousId] = useState<string | null>(() => {
    return Cookies.get(COOKIE_NAMES.anonymousId) || null
  })

  useEffect(() => {
    const updateAnonymousIdIfMissing = async () => {
      if (!anonymousId) {
        callFunctionProbabilistically({
          functionToCall: () => {
            captureException(new Error('Anonymous ID Cookie Not Found!'))
          },
          probabilityOfCalling: 0.05,
        })

        const anonymousIdFromSegmentAnalytics = (
          await segmentAnalytics.user()
        ).anonymousId()

        if (anonymousIdFromSegmentAnalytics) {
          setAnonymousId(anonymousIdFromSegmentAnalytics)
          Cookies.set(COOKIE_NAMES.anonymousId, anonymousIdFromSegmentAnalytics)
        } else {
          const newAnonymousId = createNewAnonymousId()
          await segmentAnalytics.setAnonymousId(newAnonymousId)
          Cookies.set(COOKIE_NAMES.anonymousId, newAnonymousId)
          setAnonymousId(newAnonymousId)
        }
      }
    }

    updateAnonymousIdIfMissing()
  }, [])

  return (
    <AnonymousIdContext.Provider value={anonymousId}>
      {children}
    </AnonymousIdContext.Provider>
  )
}

export const useAnonymousId = (): string | null => {
  const anonymousId = useContext(AnonymousIdContext)

  if (anonymousId === ANONYMOUS_ID_CONTEXT_INITIAL_VALUE) {
    throw new Error('useAnonymousId must be used within an AnonymousIdProvider')
  }

  return anonymousId
}
